<script lang="ts" setup>
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { CatalogPageData, CatalogResponse, MulticardProductListingData, OfferFull } from '~/types/Catalog';
import { getMulticardRedirectLocation, isMulticardGroupPath, type MulticardUrlResolution } from '~/utils/multicardSeo';

const route = useRoute();
const nuxtApp = useNuxtApp();
const catalogStore = useCatalogStore();
const { apiUrl } = useRuntimeConfig().public;
const { beginCatalogScrollRestoration, consumeCatalogScrollPosition, finishCatalogScrollRestoration } =
  useCatalogScrollRestoration();
const categoryPageData = ref<CatalogPageData | null>(null);
const productListingData = ref<MulticardProductListingData | null>(null);
const catalogRequestRevision = useState('catalog-request-revision', () => 0);
const multicardUrlResolution = useState<MulticardUrlResolution | null>('multicard-url-resolution', () => null);
const requestEvent = import.meta.server ? useRequestEvent() : null;

if (requestEvent?.context.multicardUrlResolution) {
  multicardUrlResolution.value = requestEvent.context.multicardUrlResolution as MulticardUrlResolution;
}

function getCurrentFullPath() {
  return import.meta.client
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : route.fullPath;
}

const initialRequestRevision = ++catalogRequestRevision.value;
const initialRequestedFullPath = route.fullPath;
const initialResolutionResult = await resolveCurrentMulticardGroupUrl(initialRequestRevision, initialRequestedFullPath);
let data: CatalogResponse | null = null;

if (initialResolutionResult.shouldLoadCatalog) {
  // A product-level multicard URL has no regular catalog page. Treat its 404 as
  // an expected branch and resolve it to the product listing below.
  data = await catalogStore.getCatalogData(undefined, true);
}

const initialRequestIsCurrent =
  initialRequestRevision === catalogRequestRevision.value && getCurrentFullPath() === initialRequestedFullPath;

usePublicPageLastModified(initialRequestIsCurrent ? data?.last_modified : null);

if (initialRequestIsCurrent && initialResolutionResult.shouldLoadCatalog) {
  await processData(data, initialResolutionResult.resolution);
}

async function processData(data: CatalogResponse | null, routeResolution: MulticardUrlResolution | null = null) {
  if (!data) {
    if (routeResolution?.type === 'multicard_group') {
      throwCatalogNotFound();
    }

    const resolution = routeResolution ?? (await resolveMulticardUrl());

    if (resolution?.type === 'product_base') {
      multicardUrlResolution.value = resolution;
      await loadProductListing();

      return;
    }

    if (import.meta.client) finishCatalogScrollRestoration(route.fullPath);

    return;
  }

  if (data.type === CATALOG_PAGE_TYPES.REDIRECT) {
    if (import.meta.client) finishCatalogScrollRestoration(route.fullPath);

    await nuxtApp.runWithContext(() => navigateTo(data.data.uri, { redirectCode: 301, replace: true }));

    return;
  }

  if (data.type !== CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE || !isMulticardGroupPath(route.path)) {
    multicardUrlResolution.value = null;
  }

  productListingData.value = null;

  const scrollPosition = import.meta.client && !route.hash ? consumeCatalogScrollPosition(route.fullPath) : null;

  if (scrollPosition !== null) beginCatalogScrollRestoration(route.fullPath);

  if (data.type === CATALOG_PAGE_TYPES.CATEGORY_PAGE || data.type === CATALOG_PAGE_TYPES.SEO_CATEGORY_PAGE) {
    catalogStore.currentPageType = data.type;
    categoryPageData.value = data.data as CatalogPageData;
  } else if (data.type === CATALOG_PAGE_TYPES.OFFER_PAGE || data.type === CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE) {
    catalogStore.currentPageType = data.type;
    catalogStore.selectedOffer = data.data as OfferFull;
  }

  if (import.meta.client && !route.hash) {
    if (scrollPosition === null) {
      setTimeout(() => window.scrollTo(0, 0), 10);
    } else {
      await nextTick();
      window.scrollTo({ behavior: 'instant', left: 0, top: scrollPosition });
      requestAnimationFrame(() => finishCatalogScrollRestoration(route.fullPath));
    }
  }
}

async function loadProductListing() {
  const slug = Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug;
  if (!slug) return;

  const response = await $fetch<{ type: 'product_listing'; data: MulticardProductListingData }>(
    `/catalog/listing/${slug}`,
    { baseURL: `${apiUrl}2` },
  );

  productListingData.value = response.data;
  categoryPageData.value = null;
  catalogStore.selectedOffer = null as unknown as OfferFull;
  catalogStore.currentPageType = 'product_listing';
}

function getErrorStatusCode(error: unknown) {
  return (
    (error as { status?: number | string; statusCode?: number | string }).statusCode ??
    (error as { status?: number | string }).status
  );
}

function throwCatalogNotFound(): never {
  const notFoundError = createError({ statusCode: 404, statusMessage: 'Page Not Found' });

  if (import.meta.client) {
    finishCatalogScrollRestoration(route.fullPath);
    showError(notFoundError);
  }

  throw notFoundError;
}

async function fetchMulticardUrlResolution() {
  if (import.meta.server && requestEvent?.context.multicardUrlResolution) {
    return requestEvent.context.multicardUrlResolution as MulticardUrlResolution;
  }

  const slug = Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug;
  if (!slug) return null;

  return await $fetch<{ data: MulticardUrlResolution }>(`/catalog/resolve/${slug}`, {
    baseURL: `${apiUrl}2`,
    query: route.query.offer_id ? { offer_id: String(route.query.offer_id) } : undefined,
  }).then((response) => response.data);
}

async function resolveMulticardUrl() {
  try {
    return await fetchMulticardUrlResolution();
  } catch (error: unknown) {
    const statusCode = getErrorStatusCode(error);

    if (String(statusCode) === '404') {
      throwCatalogNotFound();
    }

    throw error;
  }
}

async function resolveCurrentMulticardGroupUrl(requestRevision: number, requestedFullPath: string) {
  if (!isMulticardGroupPath(route.path)) {
    multicardUrlResolution.value = null;

    return { shouldLoadCatalog: true, resolution: null };
  }

  let resolution: MulticardUrlResolution | null;

  try {
    resolution = await fetchMulticardUrlResolution();
  } catch (error: unknown) {
    if (requestRevision !== catalogRequestRevision.value || getCurrentFullPath() !== requestedFullPath) {
      return { shouldLoadCatalog: false, resolution: null };
    }

    if (String(getErrorStatusCode(error)) === '404') {
      throwCatalogNotFound();
    }

    throw error;
  }

  if (requestRevision !== catalogRequestRevision.value || getCurrentFullPath() !== requestedFullPath) {
    return { shouldLoadCatalog: false, resolution: null };
  }

  if (!resolution) {
    throwCatalogNotFound();
  }

  multicardUrlResolution.value = resolution;

  const redirectLocation = getMulticardRedirectLocation(
    resolution,
    new URL(requestedFullPath, 'http://localhost').searchParams,
  );

  if (!redirectLocation) return { shouldLoadCatalog: true, resolution };

  await nuxtApp.runWithContext(() => navigateTo(redirectLocation, { redirectCode: 301, replace: true }));

  return { shouldLoadCatalog: false, resolution };
}

watch(
  () => [route.path, JSON.stringify(route.query)],
  async () => {
    const requestRevision = ++catalogRequestRevision.value;
    const requestedFullPath = route.fullPath;
    const resolutionResult = await resolveCurrentMulticardGroupUrl(requestRevision, requestedFullPath);

    if (!resolutionResult.shouldLoadCatalog) return;

    const data = await catalogStore.getCatalogData(undefined, true);

    if (requestRevision !== catalogRequestRevision.value || getCurrentFullPath() !== requestedFullPath) return;

    await processData(data, resolutionResult.resolution);
  },
);
</script>

<template>
  <div class="relative">
    <CategoryPage
      v-if="
        categoryPageData &&
        [CATALOG_PAGE_TYPES.CATEGORY_PAGE, CATALOG_PAGE_TYPES.SEO_CATEGORY_PAGE].includes(catalogStore.currentPageType)
      "
      :page-data="categoryPageData"
    />

    <ProductPage
      v-else-if="
        catalogStore.selectedOffer &&
        [CATALOG_PAGE_TYPES.OFFER_PAGE, CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE].includes(catalogStore.currentPageType)
      "
      :key="`${catalogStore.selectedOffer.id}-${catalogStore.selectedOffer.selected_offer_id}`"
    />

    <MulticardProductListingPage
      v-else-if="productListingData && catalogStore.currentPageType === 'product_listing'"
      :page-data="productListingData"
    />
  </div>
</template>
